import { ApplogLevel } from 'src/api/FiretrolApiClient';
import { getStatusCode } from 'src/api/queries/errorClassification';
import { DateInput, formatDateTime, getErrorMessage } from 'src/utils';

export const APPLOG_TIME_RANGE_OPTIONS = [
  { value: '15m', label: 'Last 15 minutes', minutes: 15 },
  { value: '1h', label: 'Last 1 hour', minutes: 60 },
  { value: '6h', label: 'Last 6 hours', minutes: 6 * 60 },
  { value: '24h', label: 'Last 24 hours', minutes: 24 * 60 },
  { value: '7d', label: 'Last 7 days', minutes: 7 * 24 * 60 },
] as const;

export type ApplogTimeRange = typeof APPLOG_TIME_RANGE_OPTIONS[number]['value'];

export const DEFAULT_APPLOG_TIME_RANGE: ApplogTimeRange = '1h';

export const DEFAULT_APPLOG_LEVEL = ApplogLevel.Information;

export const APPLOG_LIMIT = 200;

/** The server rejects a longer search term with a 400. */
export const APPLOG_SEARCH_MAX_LENGTH = 200;

const APPLOG_LEVEL_LABELS: Record<ApplogLevel, string> = {
  [ApplogLevel.Verbose]: 'Verbose',
  [ApplogLevel.Information]: 'Information',
  [ApplogLevel.Warning]: 'Warning',
  [ApplogLevel.Error]: 'Error',
  [ApplogLevel.Critical]: 'Critical',
};

const APPLOG_LEVELS: ApplogLevel[] = [
  ApplogLevel.Verbose,
  ApplogLevel.Information,
  ApplogLevel.Warning,
  ApplogLevel.Error,
  ApplogLevel.Critical,
];

export const APPLOG_LEVEL_OPTIONS: { value: string; label: string }[] =
  APPLOG_LEVELS.map((level) => ({
    value: String(level),
    label: APPLOG_LEVEL_LABELS[level],
  }));

const APPLOG_TIME_RANGE_MINUTES = Object.fromEntries(
  APPLOG_TIME_RANGE_OPTIONS.map((option) => [option.value, option.minutes]),
) as Record<ApplogTimeRange, number>;

export function getTimeRangeMinutes(range: ApplogTimeRange): number {
  return APPLOG_TIME_RANGE_MINUTES[range];
}

export function parseApplogTimeRange(value: string | null): ApplogTimeRange {
  const option = APPLOG_TIME_RANGE_OPTIONS.find((o) => o.value === value);
  return option ? option.value : DEFAULT_APPLOG_TIME_RANGE;
}

export function parseApplogLevel(value: string | null): ApplogLevel {
  const level = APPLOG_LEVELS.find((candidate) => String(candidate) === value);
  return level ?? DEFAULT_APPLOG_LEVEL;
}

/** The DTO field is a plain number at runtime, so the enum type is not enforced. */
export function getApplogLevelLabel(level: ApplogLevel): string {
  return APPLOG_LEVEL_LABELS[level] ?? 'Unknown';
}

export function getApplogLevelColor(level: ApplogLevel): string {
  switch (level) {
    case ApplogLevel.Critical:
      return 'grape';
    case ApplogLevel.Error:
      return 'red';
    case ApplogLevel.Warning:
      return 'yellow';
    case ApplogLevel.Information:
      return 'blue';
    default:
      return 'gray';
  }
}

const APPLOG_TIMESTAMP_FORMAT = 'MM/DD/YYYY h:mm:ss a';

export function formatApplogTimestamp(value: DateInput): string {
  return formatDateTime(value, APPLOG_TIMESTAMP_FORMAT);
}

export function getApplogsErrorMessage(error: unknown): string {
  if (getStatusCode(error) === 502) {
    return 'Azure could not be reached to read application logs. Try again in a moment.';
  }
  return getErrorMessage(error, 'Failed to load application logs.');
}
